import { useLearningUiTranslation } from '@features/learning/shared/use-learning-ui-translation'
import { useMemo, useState } from 'react'
import type { FlashcardGameTemplateProps } from '../template-types'
import { playLearningFeedbackAudio } from '@features/learning/shared/learning-feedback-audio'
import { Field, FieldLabel, LmsButton, LmsSelect } from '@shared/ui'
import {
  LearningTemplateContinueActions,
  LearningTemplateExerciseFrame,
} from '@features/learning/shared/templates'
import '../flashcard-game.css'

function getStableRank(seed: string) {
  return seed.split('').reduce((total, character) => total + character.charCodeAt(0), 0)
}

function getStableRightOptions(pairs: readonly NonNullable<FlashcardGameTemplateProps['prompt']['matchPairs']>[number][], promptId: string) {
  return [...pairs]
    .map((pair) => pair.rightLabel)
    .sort((left, right) => {
      const leftRank = getStableRank(`${promptId}:${left}`)
      const rightRank = getStableRank(`${promptId}:${right}`)

      if (leftRank === rightRank) {
        return left.localeCompare(right)
      }

      return leftRank - rightRank
    })
}

export function Template2({
  onContinue,
  prompt,
}: FlashcardGameTemplateProps) {
  const ui = useLearningUiTranslation()
  const pairs = useMemo(() => prompt.matchPairs ?? [], [prompt.matchPairs])
  const rightOptions = useMemo(
    () => getStableRightOptions(pairs, prompt.id),
    [pairs, prompt.id],
  )
  const [selections, setSelections] = useState<Record<string, string>>({})
  const [result, setResult] = useState<'correct' | 'incorrect' | null>(null)

  const handleCheckMatches = () => {
    const nextResult = pairs.every(
      (pair) => selections[pair.id] === pair.rightLabel,
    )
      ? 'correct'
      : 'incorrect'

    setResult(nextResult)
    playLearningFeedbackAudio(nextResult)
  }

  return (
    <LearningTemplateExerciseFrame
      className="flashcardGameTemplate"
      questionText={prompt.question}
      sourceOwner="flashcards"
    >
      <div className="flashcardGameMatchList">
        {pairs.map((pair) => (
          <div className="flashcardGameMatchRow" key={pair.id}>
            <div className="flashcardGameMatchContent">
              <p className="flashcardGameMatchLabel">{pair.leftLabel}</p>
              <Field className="flashcardGameMatchSelectField">
                <FieldLabel htmlFor={`flashcard-match-${pair.id}`}>
                  {ui("Match")}</FieldLabel>
                <LmsSelect
                  id={`flashcard-match-${pair.id}`}
                  onChange={(event) => {
                    setSelections((previous) => ({
                      ...previous,
                      [pair.id]: event.target.value,
                    }))
                  }}
                  value={selections[pair.id] ?? ''}
                >
                  <option value="">{ui("Choose a match")}</option>
                  {rightOptions.map((option) => (
                    <option key={`${pair.id}-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </LmsSelect>
              </Field>
            </div>
          </div>
        ))}
      </div>

      {result ? (
        <LearningTemplateContinueActions
          onContinue={onContinue}
          result={result}
        />
      ) : (
        <LmsButton
          className="flashcardGameCheckAction"
          disabled={pairs.some((pair) => !selections[pair.id])}
          onClick={handleCheckMatches}
          variant="primary"
        >
          {ui("Check matches")}</LmsButton>
      )}
    </LearningTemplateExerciseFrame>
  )
}
